<template>
  <div class="container text-with-image-card">
    <div
      class="text-with-image-card__row"
      :class="[
        `text-with-image-card__row_${theme || 'green'}`,
        {
          'text-with-image-card__row_reversed': image_from_right,
          'text-with-image-card__row_no-image': !img_src,
          'text-with-image-card__row_shadow': shadow,
        },
      ]">
      <lazy-image
        v-if="img_src"
        class="text-with-image-card__image"
        cover_img
        :src="img_src"
        :lazy_src="lazy_img_src"
        :alt="title || 'image'"/>
      <div class="text-with-image-card__content">
        <img
          v-if="corner_icon_src"
          class="text-with-image-card__corner-icon"
          :src="corner_icon_src"
          alt="">
        <img
          v-if="logo_src"
          class="text-with-image-card__logo"
          :src="logo_src"
          alt="logo">
        <span
          v-if="show_divider"
          class="text-with-image-card__divider"/>
        <!-- Cand titlul e imagine (font comercial exportat cu contururi din Figma),
             `title` ramane folosit ca text alternativ. -->
        <img
          v-if="title_image_src"
          class="text-with-image-card__title-image"
          :src="title_image_src"
          :alt="title">
        <default-title
          v-else-if="title"
          class="text-with-image-card__title"
          :class="{'text-with-image-card__title_script': title_script}"
          pre_wrap
          :title="title"
          :title_tag="title_tag"/>
        <span
          v-if="title_divider"
          class="text-with-image-card__title-divider"/>
        <html-block
          v-if="content"
          class="text-with-image-card__text"
          :class="`text-with-image-card__text_${list_style || 'default'}`"
          :content="content"/>
        <default-button
          v-if="button_text && !button_plus_link"
          class="text-with-image-card__button"
          :href="button_link"
          :target="button_new_tab ? '_blank' : null"
          :rel="button_new_tab ? 'noopener' : null">
          {{ button_text }}
        </default-button>
        <span
          v-else-if="button_text"
          class="text-with-image-card__button-group">
          <default-button
            class="text-with-image-card__button"
            :href="button_link"
            :target="button_new_tab ? '_blank' : null"
            :rel="button_new_tab ? 'noopener' : null">
            {{ button_text }}
          </default-button>
          <a
            class="text-with-image-card__button-plus"
            aria-label="More"
            :href="button_plus_link"
            :target="button_new_tab ? '_blank' : null"
            :rel="button_new_tab ? 'noopener' : null"/>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import HtmlBlock from "../small_parts/HtmlBlock";
import LazyImage from "../small_parts/LazyImage.vue";
import DefaultTitle from "../small_parts/DefaultTitle.vue";
import DefaultButton from "../slots/DefaultButton.vue";

export default {
  name: "TextWithImageCard",
  components: {
    DefaultButton,
    DefaultTitle,
    LazyImage,
    HtmlBlock
  },
  // Note despre propuri. Nu pot sta intre marcajele de mai jos: parserul citeste tot
  // ce e intre ele ca JSON, iar o linie de comentariu il face sa cedeze tacit si
  // componenta ajunge in page builder fara niciun camp editabil.
  //
  // `title_divider` — linia neagra scurta de sub titlu. E prop, nu tinuta de tema,
  //   pentru ca apare pe teme diferite (plain la "How we move", light la "Business
  //   concept").
  // `list_style`   — felul listelor din continut. Explicit, nu dedus din marcaj:
  //   pagina are deja un card cu <ul> cu buline obisnuite, care nu trebuie sa capete
  //   bife. A avut si `fishbone`, pentru "Who NOZ is?"; sectiunea aceea a trecut in
  //   `TextWithIconTiles`, si stilul odata cu ea, ca sa nu stea in doua locuri.
  // `button_plus_link` — patratul alb cu "+" de langa buton, din macheta NOZ UNIVERSE.
  //   Link separat, nu acelasi cu al butonului, fiindca inca nu stim ce face: pana
  //   raspunde VEO e pus tot pe noz.fr, dar se schimba din admin fara build. Gol
  //   inseamna fara patrat, deci celelalte instante randeaza exact ca inainte.
  // `shadow` — umbra sub card. Nu e in macheta: fundalul e alb curat pana la
  //   marginea cardului, verificat pe pixeli. A cerut-o recenzia, pentru cardul care
  //   sta peste fotografia de header si se lipea de ea. Prop, nu regula legata de
  //   `mt-minus-60`: suprapunerea vine dintr-o clasa aleasa din admin, iar cardul
  //   poate sta peste o fotografie si fara ea.
  // `button_new_tab` — deschide ambele linkuri (butonul si patratul) in tab nou.
  //   Cerut la recenzie pentru NOZ WEBSITE, care duce in afara site-ului. E caseta
  //   in admin, nu regula automata pe "link extern": alte instante ale componentei
  //   pot avea linkuri externe pe care nu le vrem in tab nou. `target` ajunge pe
  //   `<a>`-ul din DefaultButton ca atribut nedeclarat — componenta e partajata de
  //   22 de fisiere si nu se atinge.
  props: {
    /**props**/
    img_src: {
      type: String,
      default: '',
      admin_type: 'image',
    },
    lazy_img_src: {
      type: String,
      default: '',
      admin_type: 'image',
    },
    image_from_right: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
    },
    logo_src: {
      type: String,
      default: '',
      admin_type: 'image',
    },
    title: {
      type: String,
      default: '',
      admin_type: 'textarea',
    },
    title_tag: {
      type: String,
      default: 'h2',
      admin_type: 'select',
      admin_data_values: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
      ],
    },
    title_script: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
    },
    title_image_src: {
      type: String,
      default: '',
      admin_type: 'image',
    },
    corner_icon_src: {
      type: String,
      default: '',
      admin_type: 'image',
    },
    button_text: {
      type: String,
      default: '',
      admin_type: 'input',
    },
    button_link: {
      type: String,
      default: '',
      admin_type: 'input',
    },
    button_plus_link: {
      type: String,
      default: '',
      admin_type: 'input',
    },
    button_new_tab: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
      admin_text: 'Open the button links in a new tab',
    },
    shadow: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
      admin_text: 'Drop shadow under the card',
    },
    show_divider: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
    },
    title_divider: {
      type: Boolean,
      default: false,
      admin_type: 'checkbox',
    },
    list_style: {
      type: String,
      default: 'default',
      admin_type: 'select',
      admin_data_values: [
        'default',
        'checks'
      ],
    },
    content: {
      type: String,
      default: '',
      admin_type: 'editor',
    },
    theme: {
      type: String,
      default: 'green',
      admin_type: 'select',
      admin_data_values: [
        'green',
        'light',
        'noz',
        'plain'
      ],
    },
    /**props**/
  }
}
</script>
